import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setPageMeta } from '../lib/seo';

const policies = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated: April 28, 2026',
    sections: [
      {
        html: [
          'This Privacy Policy explains how <a href="https://clouniq.com" target="_blank" rel="noreferrer">CLOUNIQ</a> collects, uses, stores, and protects personal information when you use our Cloud PC and Cloud VPS services.'
        ]
      },
      {
        heading: '1. Company / Data Controller Information',
        list: [
          'Legal entity: -',
          'Registration number: 15459793',
          'Registered address: -',
          'Email: info@clouniq.com'
        ],
        text: [
          'Where required by law, this entity acts as the data controller for the personal data described in this Policy.'
        ]
      },
      {
        heading: '2. Scope',
        text: ['This Policy applies to:'],
        list: [
          'Our website and cloud services (Cloud PC and Cloud VPS),',
          'Support requests and service communications,',
          'Billing and account management,',
          'Marketing activities where legally allowed or based on consent.'
        ],
        textAfter: [
          'This Policy does not cover third-party websites or tools that are linked from our website.'
        ]
      },
      {
        heading: '3. Personal Data We Collect',
        text: ['We may collect the following categories of personal data:'],
        blocks: [
          {
            subheading: '3.1 Information you provide directly',
            list: [
              'Account information (name, email, login credentials, profile details).',
              'Billing information (billing name/address, tax details, transaction metadata).',
              'Support communications (messages, emails, files or details you share with us).'
            ]
          },
          {
            subheading: '3.2 Information collected automatically',
            list: [
              'Device and connection data (IP address, browser, operating system, language).',
              'Usage data (pages, features, clicks, timestamps, session duration).',
              'Security data (login events, failed logins, abuse/fraud indicators).'
            ]
          },
          {
            subheading: '3.3 Service and infrastructure logs',
            list: [
              'Session and instance metadata (start/end times, region, instance IDs).',
              'Performance telemetry (CPU/RAM usage, bandwidth, storage metrics).',
              'System events required for reliability, troubleshooting, and abuse prevention.'
            ]
          }
        ],
        textAfter: [
          'We do not intentionally collect private file contents from your environment unless you explicitly share them with support, or access is needed for troubleshooting with your permission where feasible.'
        ]
      },
      {
        heading: '4. Why We Process Personal Data',
        list: [
          'To create and manage accounts and provide cloud sessions,',
          'To secure the platform and prevent fraud or abuse,',
          'To process payments, invoices, and tax/accounting obligations,',
          'To provide support and operational notices,',
          'To improve service quality, performance, and reliability,',
          'To comply with legal obligations and enforce our Terms,',
          'To send marketing communications where legally allowed.'
        ]
      },
      {
        heading: '5. Legal Bases (where applicable)',
        list: [
          'Performance of a contract,',
          'Legitimate interests (security, fraud prevention, service improvement),',
          'Legal obligations (tax, accounting, compliance),',
          'Consent (for specific cookies or marketing where required).'
        ]
      },
      {
        heading: '6. How We Share Personal Data',
        blocks: [
          {
            subheading: '6.1 Service providers',
            list: [
              'Infrastructure and hosting providers,',
              'Payment processors,',
              'Support, communication, and analytics tools.'
            ],
            text: ['These providers may only process data for contracted purposes and under appropriate safeguards.']
          },
          {
            subheading: '6.2 Legal and safety disclosures',
            text: ['We may disclose data when required by law or when necessary to protect users, investigate abuse, or enforce our Terms.']
          },
          {
            subheading: '6.3 Business transfers',
            text: ['If CLOUNIQ is involved in a merger, acquisition, or reorganization, personal data may be transferred as part of that process with appropriate protections.']
          }
        ]
      },
      {
        heading: '7. International Transfers',
        text: ['Your data may be processed in countries outside your own. Where required, we use contractual and technical safeguards for cross-border transfers.']
      },
      {
        heading: '8. Data Retention',
        text: ['We keep personal data only for as long as needed to provide services, meet legal obligations, resolve disputes, and protect the platform.']
      },
      {
        heading: '9. Security Measures',
        list: [
          'Encryption in transit and, where appropriate, at rest,',
          'Access controls and least-privilege principles,',
          'Logging, monitoring, and security alerts,',
          'Backup and recovery practices.'
        ],
        textAfter: ['No internet service can be guaranteed as completely secure. Keep your credentials confidential.']
      },
      {
        heading: '10. Your Rights',
        list: [
          'Access, correction, or deletion of your personal data (subject to legal limits),',
          'Restriction or objection to certain processing,',
          'Data portability where applicable,',
          'Withdrawal of consent where processing is consent-based,',
          'Complaint to your local supervisory authority.'
        ],
        html: ['To exercise your rights, contact <a href="mailto:info@clouniq.com">info@clouniq.com</a>.']
      },
      {
        heading: '11. Cookies and Tracking',
        html: ['We use cookies and similar technologies. See our <a href="/cookies">Cookie Policy</a> for details and preference controls.']
      },
      {
        heading: '12. Children\'s Privacy',
        text: ['Our services are not intended for children. If you believe a minor submitted personal data, contact us and we will review and remove it where required.']
      },
      {
        heading: '13. Changes to This Policy',
        text: ['We may update this Policy from time to time. Any changes will be posted on the website with an updated revision date.']
      },
      {
        heading: '14. Contact',
        html: ['Privacy questions: <a href="mailto:info@clouniq.com">info@clouniq.com</a>']
      }
    ]
  },
  terms: {
    title: 'Terms of Service',
    updated: 'Last updated: April 28, 2026',
    sections: [
      {
        html: [
          'These Terms of Service ("Terms") govern your use of CLOUNIQ and our cloud services. By creating an account or using the Service, you agree to these Terms.'
        ]
      },
      {
        heading: '1. Company Information',
        list: [
          'Legal entity: -',
          'Registration number: 15459793',
          'Registered address: -',
          'Email: info@clouniq.com'
        ]
      },
      {
        heading: '2. Definitions',
        list: [
          'Cloud PC: hosted virtual desktop environment.',
          'Cloud VPS: hosted virtual private server environment.',
          'Session: paid or granted time window for service use.',
          'Content: data, files, software, and materials you upload or process in the Service.'
        ]
      },
      {
        heading: '3. Eligibility and Accounts',
        list: [
          'You must be legally able to enter into a binding contract.',
          'You must provide accurate account information.',
          'You are responsible for activity under your account and credentials.'
        ],
        textAfter: ['We may suspend access for security, compliance, or policy violations.']
      },
      {
        heading: '4. Service Description',
        text: ['CLOUNIQ provides cloud compute resources, including VM provisioning, remote access details, networking, and allocated storage, subject to availability.']
      },
      {
        heading: '5. Acceptable Use',
        text: ['You must not use the Service for illegal or abusive activity, including malware, phishing, unauthorized access attempts, spam, prohibited content, or IP infringement.']
      },
      {
        heading: '6. Your Content and Responsibility',
        list: [
          'You retain ownership of your Content.',
          'You are responsible for backups and lawful use of your workloads.',
          'You must ensure software and data licensing compliance.'
        ]
      },
      {
        heading: '7. Software Licensing',
        text: ['You are responsible for all software licenses used in your environment, including any Windows or third-party licensing requirements.']
      },
      {
        heading: '8. Security',
        list: [
          'Use strong authentication and protect credentials.',
          'Notify us promptly if you suspect compromise.',
          'Comply with platform-level security controls we apply.'
        ]
      },
      {
        heading: '9. Sessions and Availability',
        list: [
          'Sessions may start and end at scheduled times.',
          'Late connection does not guarantee extension.',
          'Maintenance or upstream outages may occur.'
        ]
      },
      {
        heading: '10. Pricing, Billing, and Taxes',
        text: ['You agree to pay all fees and applicable taxes shown at checkout or in your plan terms. Payments may be handled by third-party processors.']
      },
      {
        heading: '11. Cancellations and Refunds',
        html: ['Cancellation and refund eligibility is described at checkout and in our <a href="/refund">Refund Policy</a>.']
      },
      {
        heading: '12. Suspension and Termination',
        list: [
          'We may suspend or terminate for Terms/AUP violations, failed payment, legal requirements, or security risk.',
          'Termination may result in loss of access to your environments.'
        ]
      },
      {
        heading: '13. Disclaimer',
        text: ['The Service is provided "as is" and "as available" to the extent permitted by law.']
      },
      {
        heading: '14. Limitation of Liability',
        text: ['To the maximum extent permitted by law, CLOUNIQ is not liable for indirect or consequential damages, and aggregate liability is limited as allowed under applicable terms and law.']
      },
      {
        heading: '15. Indemnification',
        text: ['You agree to indemnify CLOUNIQ against claims arising from your Content, your use of the Service, or your violation of law or these Terms.']
      },
      {
        heading: '16. Changes to the Terms',
        text: ['We may update these Terms. Continued use after updates means you accept the revised Terms.']
      },
      {
        heading: '17. Contact',
        html: ['Questions about these Terms: <a href="mailto:info@clouniq.com">info@clouniq.com</a>']
      }
    ]
  },
  refund: {
    title: 'Refund Policy',
    updated: 'Last updated: April 28, 2026',
    sections: [
      {
        text: ['This Refund Policy describes when CLOUNIQ may issue full or partial refunds for Cloud PC and Cloud VPS purchases.']
      },
      {
        heading: '1. General Principles',
        text: ['Because we provide time-based digital services, refund decisions depend on service delivery, resource reservation, and actual usage.']
      },
      {
        heading: '2. When Refunds May Apply',
        list: [
          'Verified platform-side outage that blocked meaningful access,',
          'Confirmed billing or charging error,',
          'Cancellation within an eligible cancellation window (if offered).'
        ]
      },
      {
        heading: '3. Non-Refundable Cases',
        list: [
          'Issues caused by local device, network, firewall, or user-side configuration,',
          'Problems from user-installed software,',
          'Used sessions unless required by law,',
          'Abuse, fraud, or Terms/AUP violations.'
        ]
      },
      {
        heading: '4. Partial Refunds or Credit',
        text: ['Where appropriate, we may issue pro-rated refunds or account credit based on verified impact.']
      },
      {
        heading: '5. How to Request a Refund',
        html: ['Send your request to <a href="mailto:info@clouniq.com">info@clouniq.com</a> and include order reference, session date/time, issue details, and any screenshots or error messages.']
      },
      {
        heading: '6. Review and Payout',
        text: ['We may request additional details before making a decision. Approved refunds are returned to the original payment method where possible, or issued as account credit.']
      },
      {
        heading: '7. Chargebacks',
        text: ['If a chargeback is initiated, we may suspend access while the case is investigated. Fraudulent chargebacks may lead to permanent account termination.']
      },
      {
        heading: '8. Statutory Rights',
        text: ['Nothing in this Policy limits mandatory consumer rights under applicable law.']
      }
    ]
  },
  shipping: {
    title: 'Shipping Policy',
    updated: 'Last updated: April 28, 2026',
    sections: [
      {
        text: ['CLOUNIQ provides digital services only. We do not ship physical products.']
      },
      {
        heading: '1. No Physical Shipping',
        list: [
          'No shipping fees apply.',
          'No physical delivery is provided.'
        ]
      },
      {
        heading: '2. Digital Delivery',
        list: [
          'Access details are delivered digitally (dashboard and/or email).',
          'Credentials are provided before session start where applicable.'
        ]
      },
      {
        heading: '3. Delivery Issues',
        text: ['If access details are missing, check spam/junk folders, verify your account email, and contact support with your order ID.']
      }
    ]
  },
  cookies: {
    title: 'Cookie Policy',
    updated: 'Last updated: April 28, 2026',
    sections: [
      {
        html: ['This Cookie Policy explains how CLOUNIQ uses cookies and similar technologies on <a href="https://www.clouniq.com" target="_blank" rel="noreferrer">www.clouniq.com</a>.']
      },
      {
        heading: '1. What Are Cookies?',
        text: ['Cookies are small files stored on your device. Similar technologies may include local storage, pixels, and related tracking tools.']
      },
      {
        heading: '2. Why We Use Cookies',
        list: [
          'Essential functionality (login, security, session handling),',
          'Preferences (language, UI settings),',
          'Analytics (improving reliability and product experience),',
          'Marketing support where permitted and applicable.'
        ]
      },
      {
        heading: '3. Types of Cookies',
        blocks: [
          {
            subheading: '3.1 Strictly Necessary',
            text: ['Required to run core website and account functionality.']
          },
          {
            subheading: '3.2 Performance and Analytics',
            text: ['Help us understand site usage and improve performance.']
          },
          {
            subheading: '3.3 Functionality',
            text: ['Remember user choices and interface preferences.']
          },
          {
            subheading: '3.4 Marketing (Optional)',
            text: ['Used to measure campaigns and improve relevance, where permitted.']
          }
        ]
      },
      {
        heading: '4. Managing Cookies',
        list: [
          'Use cookie-banner controls where available,',
          'Adjust browser settings to block/delete cookies,',
          'Use privacy tools or extensions.'
        ],
        textAfter: ['Disabling essential cookies may prevent login or key features from working.']
      },
      {
        heading: '5. Third-Party Cookies',
        text: ['Some providers (analytics, payment, embedded services) may set cookies. Their own privacy policies govern their processing.']
      },
      {
        heading: '6. Do Not Track',
        text: ['Some browsers send Do Not Track signals. Because standards vary, handling may differ by environment, but we comply with applicable consent requirements.']
      },
      {
        heading: '7. Policy Updates',
        text: ['We may update this Cookie Policy from time to time and publish changes on the website.']
      },
      {
        heading: '8. Contact',
        html: ['Cookie/privacy questions: <a href="mailto:info@clouniq.com">info@clouniq.com</a>']
      }
    ]
  }
};
const policyNav = [
  { key: 'privacy', label: 'Privacy', to: '/privacy' },
  { key: 'terms', label: 'Terms', to: '/terms' },
  { key: 'refund', label: 'Refund', to: '/refund' },
  { key: 'shipping', label: 'Shipping', to: '/shipping' },
  { key: 'cookies', label: 'Cookies', to: '/cookies' },
];

const LegalPage = ({ policyKey }) => {
  const policy = policies[policyKey];

  useEffect(() => {
    if (policy) {
      setPageMeta({
        title: `${policy.title} � Clouniq`,
        description: `${policy.title} for CLOUNIQ services.`,
      });
    } else {
      setPageMeta({
        title: 'Legal | Clouniq',
        description: 'Review clouniq legal terms, privacy policy, cookie policy, and refunds.',
      });
    }
  }, [policy]);

  if (!policy) {
    return (
      <section className="policy-section">
        <div className="policy-container">
          <div className="policy-card glass">
            <h1 className="policy-title">Policy not found</h1>
          </div>
        </div>
      </section>
    );
  }

  const normalizedSections = policy.sections.map((section, idx) => {
    const rawTitle = section.heading || section.subheading || `Section ${idx + 1}`;
    const id = `section-${idx + 1}-${String(rawTitle).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return { ...section, id, rawTitle };
  });

  return (
    <section className="policy-section nova-legal-page">
      <div className="policy-container">
        <div className="policy-card glass nova-legal-shell">
          <div className="policy-header nova-legal-hero">
            <div>
              <span className="section-tag">Legal Center</span>
              <h1 className="policy-title">{policy.title}</h1>
              <p className="policy-updated">{policy.updated}</p>
              <div className="nova-legal-policy-nav" aria-label="Policy navigation">
                {policyNav.map((item) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    className={`nova-legal-policy-link ${item.key === policyKey ? 'active' : ''}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="nova-legal-layout">
            <aside className="nova-legal-index">
              <h3>On This Page</h3>
              <ul>
                {normalizedSections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>{section.rawTitle}</a>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="policy-body nova-legal-body">
              {normalizedSections.map((section, idx) => (
                <div className="policy-block nova-legal-block" key={idx} id={section.id}>
                {section.heading && <h2 className="policy-heading">{section.heading}</h2>}
                {section.subheading && (
                  <h3 className="policy-subheading">{section.subheading}</h3>
                )}
                {section.subheadings && section.subheadings.map((sub, i) => (
                  <h3 className="policy-subheading" key={`sub-${i}`}>{sub}</h3>
                ))}
                {section.text && section.text.map((t, i) => (
                  <p className="policy-text" key={i}>{t}</p>
                ))}
                {section.html && section.html.map((t, i) => (
                  <p
                    className="policy-text"
                    key={`html-${i}`}
                    dangerouslySetInnerHTML={{ __html: t }}
                  />
                ))}
                {section.blocks && section.blocks.map((block, bIdx) => (
                  <div className="policy-subblock" key={`block-${bIdx}`}>
                    {block.subheading && (
                      <h3 className="policy-subheading">{block.subheading}</h3>
                    )}
                    {block.text && block.text.map((t, i) => (
                      <p className="policy-text" key={`block-text-${bIdx}-${i}`}>{t}</p>
                    ))}
                    {block.html && block.html.map((t, i) => (
                      <p
                        className="policy-text"
                        key={`block-html-${bIdx}-${i}`}
                        dangerouslySetInnerHTML={{ __html: t }}
                      />
                    ))}
                    {block.list && (
                      <ul className="policy-list">
                        {block.list.map((item, i) => (
                          <li key={`block-li-${bIdx}-${i}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {section.textAfter && section.textAfter.map((t, i) => (
                  <p className="policy-text" key={`after-${i}`}>{t}</p>
                ))}
                {section.list && (
                  <ul className="policy-list">
                    {section.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalPage;






